'use client';

import { NewGeneralDialog } from '@/components/NewGeneralDialog';
import { StepperStep } from '@/components/NewGeneralDialog/DialogStepper';
import { GTMEventName } from '@/constants/gtm';
import { ROUTES } from '@/constants/routes';
import { useAcceptTermsOfService } from '@/hooks/useAcceptTermsOfService';
import { useCredentialsSignup } from '@/hooks/useCredentialsSignup';
import { useSendGTMEvent } from '@/hooks/useSendGTMEvents';
import { SignUpFormData } from '@/types/auth';
import { Stack } from '@mui/material';
import { EMAIL_REGEX } from '@treyd-io/core/constants/regex';
import { useToastNotification } from '@treyd-io/treyd-ui/hooks/useToastNotification';
import { FormikHelpers, useFormik } from 'formik';
import { AnimatePresence } from 'framer-motion';
import { t } from 'i18next';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import * as Yup from 'yup';
import { DisclaimerStep } from './(wizard)/DisclaimerStep';
import { SignupStep } from './(wizard)/SignupStep';
enum STEPS {
  DisclaimerStep,
  SignupStep,
}

const Signup = () => {
  const showToastMessage = useToastNotification();
  const { sendEvent } = useSendGTMEvent();

  const [currentStep, setCurrentStep] = useState(STEPS.DisclaimerStep);
  const [onNext, setOnNext] = useState(true);
  const { mutate } = useCredentialsSignup();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { mutate: acceptTermsOfService } = useAcceptTermsOfService();
  const router = useRouter();
  const getCharacterValidationError = (str: string) =>
    `${t('your_password_must_have_at_least_one')} ${str} ${t('character')}`;

  const form = useFormik<SignUpFormData>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
    },
    validationSchema: Yup.object({
      firstName: Yup.string()
        .max(15, 'Must be 15 characters or less')
        .required('Required'),
      lastName: Yup.string()
        .max(20, 'Must be 20 characters or less')
        .required('Required'),
      email: Yup.string()
        .required(t('email_is_required'))
        .trim()
        .matches(EMAIL_REGEX, t('invalid_email_address')),
      password: Yup.string()
        .required(t('password_is_required'))
        .min(8, t('password_must_have_at_least_eight_characters'))
        .matches(/[0-9]/, getCharacterValidationError(t('digit')))
        .matches(/[a-z]/, getCharacterValidationError(t('lowercase')))
        .matches(/[A-Z]/, getCharacterValidationError(t('uppercase'))),
    }),
    onSubmit: (values, formikHelpers) => {
      handleSubmit(values, formikHelpers);
    },
  });

  const handleNextStep = () => {
    setOnNext(true);
    setTimeout(() => {
      setCurrentStep(currentStep + 1);
    }, 100);
  };

  const handlePreviousStep = () => {
    setOnNext(false);
    setTimeout(() => {
      setCurrentStep(currentStep - 1);
    }, 100);
  };

  const handleSubmit = async (
    values: SignUpFormData,
    formikHelpers: FormikHelpers<SignUpFormData>
  ) => {
    if (!executeRecaptcha) return;

    const recaptchaToken = await executeRecaptcha();
    mutate(
      {
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email?.toLowerCase(),
        password1: values.password,
        password2: values.password,
        token: recaptchaToken,
      },
      {
        onSuccess: ({ data }) => {
          acceptTermsOfService(data?.access_token, {
            onSuccess: () => {
              signIn('accessToken', {
                accessToken: data?.access_token,
                refreshToken: data?.refresh_token,
              });
              sendEvent({
                event: GTMEventName.CREDENTIALS_SIGNUP,
                user_email: values.email,
              });
            },
          });
        },
        onError: (error) => {
          if (error.response?.data) {
            formikHelpers.setErrors(error.response?.data);
            if (error.response?.data?.password1) {
              formikHelpers.setFieldError(
                'password',
                error.response?.data?.password1
              );
            }
          } else {
            return showToastMessage({
              message: 'Something went wrong!',
              type: 'error',
            });
          }
        },
        onSettled: () => {
          formikHelpers.setSubmitting(false);
        },
      }
    );
  };

  const actionMenuSteps: StepperStep[] = [
    {
      label: t('sign_up'),
      status: 'not_completed',
    },
    {
      label: t('phone_verification'),
      status: 'not_completed',
    },
    {
      label: t('identity_verification'),
      status: 'not_completed',
    },
    {
      label: t('company_details'),
      status: 'not_completed',
    },
  ];

  return (
    <Stack direction="column" minHeight="100vh" padding="1.5rem">
      <NewGeneralDialog
        isOpen
        steps={actionMenuSteps}
        initialActiveStepIndex={0}
        slotsProps={{
          dialogContent: {
            dividers: true,
          },
        }}
        onClose={() => router.replace(ROUTES.login)}>
        <AnimatePresence mode="popLayout">
          <Stack
            margin="auto"
            maxWidth={960}
            key={currentStep}
            marginBottom={4}>
            {currentStep === STEPS.DisclaimerStep && (
              <DisclaimerStep handleNextStep={handleNextStep} onNext={onNext} />
            )}
            {currentStep === STEPS.SignupStep && (
              <SignupStep
                form={form}
                handlePreviousStep={handlePreviousStep}
                onNext={onNext}
              />
            )}
          </Stack>
        </AnimatePresence>
      </NewGeneralDialog>
    </Stack>
  );
};

export default Signup;
